/**
 * Phase 6 ILLUSTRATIVE operator-workflow sample data. PaCC does not yet
 * persist Inbox state / Alert rules / Pins / Annotations — this file
 * demonstrates the workflow loop only. Real state lives in:
 *   - audit-trail-design.md (Phase 7.x build) for InboxState
 *   - data-retention.md     for Pin / Annotation persistence
 *
 * Every record carries source + asOf; stale pins flag; alerts distinguish
 * data-update from source-degradation events.
 *
 * NEVER use this data for operational decisions.
 */

import type {
  InboxItem, AlertRule, Watchlist, Annotation, Comparable, PathogenFamily,
} from './types';

const HR = 3_600_000;
const DAY = 86_400_000;

/** Hours/days-ago helper. Stable now; for tests pass an explicit nowMs. */
function hAgo(h: number, nowMs = Date.now()): string {
  return new Date(nowMs - h * HR).toISOString();
}
function dAgo(d: number, nowMs = Date.now()): string {
  return new Date(nowMs - d * DAY).toISOString();
}

const FAM: Record<string, PathogenFamily> = {
  ebola: 'haemorrhagic', marburg: 'haemorrhagic', lassa: 'haemorrhagic',
  nipah: 'zoonotic', mpox: 'zoonotic',
  h5n1: 'respiratory', 'sars-cov-2': 'respiratory', measles: 'respiratory', rsv: 'respiratory',
  cholera: 'enteric',
  dengue: 'vector',
};

const COMMON: Record<string, string> = {
  ebola: 'Ebola (Zaïre)', marburg: 'Marburg', lassa: 'Lassa fever',
  nipah: 'Nipah', mpox: 'Mpox (clade I)',
  h5n1: 'Influenza A(H5N1)', 'sars-cov-2': 'SARS-CoV-2', measles: 'Measles', rsv: 'RSV',
  cholera: 'Cholera', dengue: 'Dengue',
};

const IDC: Record<string, string> = {
  ebola: 'var(--bm-id-7)', marburg: 'var(--bm-id-5)', lassa: 'var(--bm-id-2)',
  nipah: 'var(--bm-id-8)', mpox: 'var(--bm-id-4)',
  h5n1: 'var(--bm-id-6)', 'sars-cov-2': 'var(--bm-id-1)',
  measles: 'var(--bm-id-1)', rsv: 'var(--bm-id-4)',
  cholera: 'var(--bm-id-3)', dengue: 'var(--bm-id-3)',
};

let _i = 0;
function item(
  pathogen: string, admin: string, iso3: string, region: string,
  tier: 0 | 1 | 2 | 3 | 4, change: InboxItem['change'], detail: string,
  hoursAgo: number, source: string, asOfDays: number,
  opts: Partial<Pick<InboxItem, 'covGap' | 'novel' | 'unverified' | 'dataQuality'>> = {},
): InboxItem {
  return {
    id: 'in' + ++_i,
    pathogen,
    common: COMMON[pathogen] ?? pathogen,
    family: FAM[pathogen] ?? 'respiratory',
    idc: IDC[pathogen] ?? 'var(--bm-id-1)',
    admin, iso3, region, tier, change, detail,
    ts: hAgo(hoursAgo),
    source,
    asOf: dAgo(asOfDays),
    state: 'new',
    ...opts,
  };
}

export const SAMPLE_INBOX: InboxItem[] = [
  item('ebola', 'North Kivu, DR Congo', 'COD', 'AFRO', 1, 'escalation', 'Cases 112 → 142 (+27%) in 48h; 3 new health zones', 3, 'WHO DON', 0, { covGap: true }),
  item('nipah', 'Rajshahi, Bangladesh', 'BGD', 'SEARO', 1, 'new', 'First detection this season — 6 probable cases', 6, 'WHO DON', 0, { novel: true, covGap: true }),
  item('marburg', 'Kigali, Rwanda', 'RWA', 'AFRO', 1, 'escalation', 'Tier held CRITICAL; 5 new cases, 2 deaths', 9, 'WHO DON', 1),
  item('h5n1', 'California, USA', 'USA', 'AMRO', 2, 'degraded', 'CDC NWSS feed DEGRADED — 9 days since last update (cadence 7d)', 2, 'CDC NWSS', 9, { dataQuality: true }),
  item('mpox', 'Kinshasa, DR Congo', 'COD', 'AFRO', 3, 'escalation', 'Tier ELEVATED → was WATCH; 389 cases since onset', 12, 'Africa CDC', 1),
  item('cholera', 'Khartoum, Sudan', 'SDN', 'EMRO', 2, 'coverage', 'Coverage gap opened — HIGH threat, Silent media at 7d', 14, 'WHO DON', 1, { covGap: true }),
  item('measles', 'Birmingham, England', 'GBR', 'EURO', 3, 'escalation', 'Cases 52 → 64 (+23%); under-vaccinated cluster', 18, 'UKHSA', 0),
  item('lassa', 'Plateau, Nigeria', 'NGA', 'AFRO', 2, 'new', 'New outbreak event notification — unverified', 20, 'NCDC', 0, { unverified: true }),
  item('rsv', 'Sydney NSW, Australia', 'AUS', 'WPRO', 3, 'escalation', 'NSW respiratory pill WATCH → ELEVATED overnight', 7, 'NSW Health', 0),
  item('dengue', 'Bangkok, Thailand', 'THA', 'SEARO', 4, 'deescalation', 'Cases falling — 4200 → 3800 WoW', 26, 'OpenDengue', 2),
  item('sars-cov-2', 'London, England', 'GBR', 'EURO', 3, 'degraded', 'covSPECTRUM genomic feed auth error — sequences stale', 30, 'covSPECTRUM', 4, { dataQuality: true }),
];

/** Hours since the operator's last visit — drives the Slack-style divider. */
export const SAMPLE_LAST_VISIT_HOURS = 10;

export const SAMPLE_RULES: AlertRule[] = [
  { id: 'r1', name: 'Any CRITICAL/HIGH in WHO region of interest', on: true, starter: true,
    when: [{ f: 'tier', op: '≥', v: 'HIGH' }, { f: 'region', op: 'in', v: 'AFRO, EMRO' }],
    notify: ['inbox', 'email'], firedLastWk: 14, kind: 'real' },
  { id: 'r2', name: 'New pathogen first-detection (any region)', on: true, starter: true,
    when: [{ f: 'change', op: '=', v: 'first-detection' }],
    notify: ['inbox', 'push'], firedLastWk: 2, kind: 'real' },
  { id: 'r3', name: 'Any source DEGRADED > 2× cadence', on: true, starter: true,
    when: [{ f: 'source.state', op: '=', v: 'DEGRADED' }],
    notify: ['inbox'], firedLastWk: 6, kind: 'quality' },
  { id: 'r4', name: 'Coverage gap opens on HIGH+ outbreak', on: true, starter: true,
    when: [{ f: 'tier', op: '≥', v: 'HIGH' }, { f: 'coverage.asymmetry', op: '=', v: 'under-reported' }],
    notify: ['inbox'], firedLastWk: 3, kind: 'real' },
  { id: 'r5', name: 'Genomic variant share > 20% in any region', on: false, starter: true,
    when: [{ f: 'variant.share', op: '>', v: '20%' }],
    notify: ['inbox'], firedLastWk: 1, kind: 'real' },
  { id: 'r6', name: 'Haemorrhagic fever anywhere, any tier', on: false, starter: true,
    when: [{ f: 'family', op: '=', v: 'haemorrhagic' }],
    notify: ['inbox', 'email'], firedLastWk: 9, kind: 'real' },
  { id: 'r7', name: 'NSW respiratory pill → ELEVATED+', on: true, starter: false,
    when: [{ f: 'region.admin', op: '=', v: 'NSW, AU' }, { f: 'metric', op: '=', v: 'respiratory' }, { f: 'tier', op: '≥', v: 'ELEVATED' }],
    notify: ['inbox', 'push'], firedLastWk: 1, kind: 'real' },
];

export const SAMPLE_WATCHLISTS: Watchlist[] = [
  {
    id: 'wl1', name: 'AFRO duty desk', pins: [
      { id: 'p1', type: 'outbreak', label: 'Ebola · North Kivu', pathogen: 'ebola', tier: 1, stale: false, asOfDays: 0 },
      { id: 'p2', type: 'outbreak', label: 'Mpox · Kinshasa', pathogen: 'mpox', tier: 3, stale: false, asOfDays: 1 },
      { id: 'p3', type: 'country', label: 'Sudan (all signals)', pathogen: 'cholera', tier: 2, stale: false, asOfDays: 1 },
    ],
  },
  {
    id: 'wl2', name: 'Genomic watch', pins: [
      { id: 'p4', type: 'pathogen', label: 'H5N1 · global', pathogen: 'h5n1', tier: 2, stale: true, asOfDays: 9 },
      { id: 'p5', type: 'signal', label: 'SARS-CoV-2 LP.8.1 · DC', pathogen: 'sars-cov-2', tier: 4, stale: true, asOfDays: 4 },
    ],
  },
];

export const SAMPLE_ANNOTATIONS: Annotation[] = [
  {
    id: 'an1', target: 'Ebola · North Kivu', pathogen: 'ebola',
    body: 'Cross-border risk into **Uganda** — ping Kampala focal point.\n- Vax stock confirmed 12k doses\n- Ring vaccination starts Thu',
    edited: hAgo(4), touchedDays: 0,
  },
  {
    id: 'an2', target: 'H5N1 · California', pathogen: 'h5n1',
    body: 'Conflicting counts: CDC 18 vs state PH 11. _Awaiting reconciliation._ Do not cite in brief until resolved.',
    edited: hAgo(50), touchedDays: 2,
  },
  {
    id: 'an3', target: 'Mpox · Kinshasa', pathogen: 'mpox',
    body: 'Clade I — watch for travel-linked exports. Last reviewed before the weekend.',
    edited: dAgo(8), touchedDays: 8, decayed: true,
  },
];

/** Synthetic incidence-ish series for Comparison mode. */
function series(peak: number, start: number, growth: number, n = 28): number[] {
  const a: number[] = [];
  let v = start;
  for (let i = 0; i < n; i++) {
    v = v * growth + Math.sin(i / 3) * peak * 0.02;
    a.push(Math.max(0, Math.round(v)));
    if (v > peak) v = peak * (0.96 + Math.sin(i) * 0.02);
  }
  return a;
}

export const SAMPLE_COMPARABLES: Comparable[] = [
  { id: 'c1', label: 'Mpox · Nigeria', sub: 'this season', pathogen: 'mpox', tier: 3, color: 'var(--bm-id-4)', data: series(420, 8, 1.22), peakDay: 19, metric: 'cases/day' },
  { id: 'c2', label: 'Mpox · DR Congo', sub: 'this season', pathogen: 'mpox', tier: 3, color: 'var(--bm-id-7)', data: series(640, 14, 1.20), peakDay: 21, metric: 'cases/day' },
  { id: 'c3', label: 'Mpox · Nigeria', sub: '2024 baseline', pathogen: 'mpox', tier: 0, color: 'var(--bm-text-subtle)', data: series(210, 6, 1.14), peakDay: 23, metric: 'cases/day', baseline: true },
];

/** Re-export the family + colour maps so the components can render typed-pin
 *  glyphs without taking a dependency on the sample data structure. */
export { FAM, COMMON, IDC };
